import type { PointerEvent as ReactPointerEvent } from "react";
import { useRef } from "react";

const DEFAULT_DURATION = 500;
const DEFAULT_MOVE_THRESHOLD = 10;

export type TouchLongPressPosition = { clientX: number; clientY: number };

export type UseTouchLongPressOptions = {
    onLongPress: (position: TouchLongPressPosition) => void;
    duration?: number;
    moveThreshold?: number;
};

type LongPressState = {
    startPos: [number, number];
    timer: ReturnType<typeof setTimeout>;
};

export function useTouchLongPress({
    onLongPress,
    duration = DEFAULT_DURATION,
    moveThreshold = DEFAULT_MOVE_THRESHOLD,
}: UseTouchLongPressOptions) {
    const stateRef = useRef<LongPressState | undefined>();
    const longPressTriggeredRef = useRef(false);
    const onLongPressRef = useRef(onLongPress);
    onLongPressRef.current = onLongPress;

    const cancel = () => {
        if (stateRef.current) {
            clearTimeout(stateRef.current.timer);
            stateRef.current = undefined;
        }
    };

    const onPointerDown = (e: ReactPointerEvent) => {
        if (e.pointerType !== "touch") {
            return;
        }

        cancel();

        if (!e.isPrimary) {
            return;
        }

        longPressTriggeredRef.current = false;

        const startPos: [number, number] = [e.clientX, e.clientY];
        stateRef.current = {
            startPos,
            timer: setTimeout(() => {
                stateRef.current = undefined;
                longPressTriggeredRef.current = true;
                onLongPressRef.current({ clientX: startPos[0], clientY: startPos[1] });
            }, duration),
        };
    };

    const onPointerMove = (e: ReactPointerEvent) => {
        if (!stateRef.current || e.pointerType !== "touch" || !e.isPrimary) {
            return;
        }

        const [sx, sy] = stateRef.current.startPos;
        const dx = e.clientX - sx;
        const dy = e.clientY - sy;
        if (Math.hypot(dx, dy) > moveThreshold) {
            cancel();
        }
    };

    const onPointerUp = (e: ReactPointerEvent) => {
        if (e.pointerType !== "touch") {
            return;
        }
        cancel();
    };

    return {
        handlers: {
            onPointerDown,
            onPointerMove,
            onPointerUp,
            onPointerCancel: onPointerUp,
        },
        wasLongPressTriggered: () => longPressTriggeredRef.current,
        cancel,
    };
}
